import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import CancelButton from "../../../components/cancelButton/CancelButton";
import AsyncLoadingButton from "../../../components/asyncLoadingButton/AsyncLoadingButton";
import ProviderSelect from "../ProviderSelect";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDownloadFile } from "../../../components/downloadFileButton/useDownloadFile";
import { downloadCsvWithPrices } from "./productPricesExportApi";
import { useSnackbar } from "notistack";
import type { PriceType } from "../priceType";
import { Select } from "../../../components/select/Select";
import dayjs from "../../../dayjs";

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

interface DownloadFileType {
  readonly year: number;
  readonly providerId: string;
  readonly priceType: PriceType;
}

export const PriceListCsvExportDialog: FC<Props> = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [providerId, setProviderId] = useState<string>();
  const [priceType, setPriceType] = useState<PriceType>("purchase");
  const [year, setYear] = useState(dayjs());
  const { download } = useDownloadFile<DownloadFileType>({
    apiDefinition: (key, r) => downloadCsvWithPrices(r!),
    onError: (error) => {
      enqueueSnackbar(`Nie udało się pobrać CSV. Powód: ${(error as Error).message}`, { variant: "error" });
    },
    filename: (key, r) => (r ? `Cennik ${r.priceType} ${r.year} - ${r.providerId}.csv` : ""),
  });
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Eksport cenników do CSV</DialogTitle>
      <DialogContent>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            marginTop: "8px",
          }}
        >
          <Select<PriceType>
            label="Typ cennika"
            fullWidth
            value={priceType}
            values={[
              { value: "purchase", label: "Ceny zakupu" },
              {
                value: "selling",
                label: "Ceny sprzedaży",
              },
              { value: "fit", label: "Ceny FIT" },
            ]}
            onSelect={(v) => setPriceType(v)}
          />
          <ProviderSelect providerId={providerId} onChange={setProviderId} />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={["year"]}
              openTo="year"
              label="Rok"
              slotProps={{
                textField: { fullWidth: true },
              }}
              minDate={dayjs().subtract(5, "years")}
              maxDate={dayjs().add(3, "years")}
              value={year}
              onChange={(v) => {
                if (v) {
                  setYear(v);
                }
              }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose} />
        <AsyncLoadingButton
          disabled={!year || !providerId}
          onClick={async () => {
            await download(undefined, { providerId: providerId!, year: year.year(), priceType });
            onClose();
          }}
        >
          Pobierz
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};
